import React, { useCallback, useEffect, useMemo, useState } from "react";
import { css } from "@emotion/react";
import { isEmpty, trim } from "lodash";
import { gql, useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import EmailValidator from "email-validator";

import { useAppState } from "core/contexts/AppContext";
import { LOGIN_URL } from "core/config/config";
import ChangeValue from "core/Components/Management/ChangeValue";
import RoundButton from "core/Components/Buttons/RoundButton";
import { Validator } from "core/hooks/useForm";
import window from 'core/includes/window';

type DataChanging = {
    key: string,
    label: string,
}

type ChangeValueProps = {
    inputs: Array<InputConfig>,
    mutation: any,
    validator?: Validator,
}

const Manage: React.FC = () => {
    const [appState] = useAppState();
    const user = appState?.user;
    const [searchParams, setSearchParams] = useSearchParams();
    const [dataChanging, setDataChanging] = useState<DataChanging|null>(null);
    const { data: eventsData } = useQuery(ORDERED_ONLINE_EVENTS_QUERY, {
        variables: {
            userId: user?.id,
        },
    });

    const { data: ordersData } = useQuery(ORDERS_QUERY, {
        variables: {
            userId: user?.id,
        },
    });

    const orderedOnlineEvents = eventsData?.orderedOnlineEvents;

    const orders = ordersData?.orders;

    useEffect(() => {
        if (!searchParams.get("tabSelected")) {
            setSearchParams({ tabSelected: tabs.ACCOUNT_OVERVIEW.key });
        }
    }, [searchParams, setSearchParams]);

    const tabSelected = Object.values(tabs).find(t => (
        t.key === searchParams.get("tabSelected"))
    ) ?? Object.values(tabs)[0];

    const emailValidator = useCallback((fields: any, isSubmit: boolean = false) => {
        const errors: any = {};

        if (!fields.email) {
            errors.email = 'Please enter your new email.'
        }

        if (isSubmit && fields.email && !EmailValidator.validate(fields.email)) {
            errors.email = 'This email is invalid.'
        }

        if (isSubmit && !errors.email && fields.email === appState?.user?.Email) {
            errors.email = "Your new email must be different to your current email."
        }

        if (!fields.confirmEmail) {
            errors.confirmEmail = 'Please confirm your new email.'
        }

        if (
            isSubmit &&
            (fields.email !== fields.confirmEmail) &&
            (!isEmpty(fields.email) && !isEmpty(fields.confirmEmail))
        ) {
            errors.confirmEmail = "These emails don’t match."
        }

        if (!fields.currentPassword) {
            errors.currentPassword = 'Please enter your password to continue.'
        }

        return errors
    }, [appState]);

    const changeValueProps: ChangeValueProps = useMemo(() => {
        const nameInputs = {
            inputs: [
                {
                    camelKey: "firstName",
                    initialValue: appState?.user?.FirstName ?? "",
                    label: "First Name",
                    type: "text",
                },
                {
                    camelKey: "surname",
                    initialValue: appState?.user?.Surname ?? "",
                    label: "Last Name",
                    type: "text",
                },
            ],
            mutation: CHANGE_NAME_MUTATION,
        };

        if (!dataChanging) return nameInputs;

        switch(true) {
            case dataChanging.key === data.NAME.key:
                return nameInputs;

            case dataChanging.key === data.EMAIL.key:
                return {
                    inputs: [
                        {
                            camelKey: "currentPassword",
                            initialValue: "",
                            label: "Current Password",
                            type: "password"
                        },
                        {
                            camelKey: "email",
                            initialValue: "",
                            label: "Email",
                            type: "text"
                        },
                        {
                            camelKey: "confirmEmail",
                            initialValue: "",
                            label: "Confirm Email",
                            type: "text"
                        },
                        {
                            camelKey: "logoutAllDevices",
                            initialValue: false,
                            label: "Log out all devices?",
                            type: "checkbox"
                        },
                    ],
                    validator: emailValidator,
                    mutation: CHANGE_EMAIL_MUTATION,
                }

            case dataChanging.key === data.PASSWORD.key:
                return {
                    inputs: [
                        {
                            camelKey: "currentPassword",
                            initialValue: "",
                            label: "Current Password",
                            type: "password"
                        },
                        {
                            camelKey: "setPassword",
                            initialValue: "",
                            label: "New Password",
                            type: "password"
                        },
                        {
                            camelKey: "confirmPassword",
                            initialValue: "",
                            label: "Confirm Password",
                            type: "password"
                        },
                        {
                            camelKey: "logoutAllDevices",
                            initialValue: false,
                            label: "Log out all devices?",
                            type: "checkbox"
                        },
                    ],
                    validator: passwordValidator,
                    mutation: CHANGE_PASSWORD_MUTATION,
                }

            default:
                return nameInputs;
        }
    }, [dataChanging, appState, emailValidator]);

    const onLogOut = useCallback(() => {
        appState.clearUser();
        window.location.href = LOGIN_URL;
    }, [appState]);

    return (
        <div css={style}>
            <div css={tabLinksStyle}>
                <Link to={`/profile?tabSelected=${tabs.ACCOUNT_OVERVIEW.key}`}>Account Overview</Link>
                <Link to={`/profile?tabSelected=${tabs.VIDEOS.key}`}>Videos</Link>
                <Link to={`/profile?tabSelected=${tabs.ORDERS.key}`}>Orders</Link>
            </div>
            <div>
                {tabSelected.key === tabs.ACCOUNT_OVERVIEW.key && (
                    <div css={accountOverviewContainerStyle}>
                        <h1 css={titleStyle}>{tabSelected.label}</h1>
                        {dataChanging ? (
                            <ChangeValue
                                label={dataChanging.label}
                                inputs={changeValueProps.inputs}
                                onFinish={() => setDataChanging(null)}
                                mutation={changeValueProps.mutation}
                                {...changeValueProps.validator && {
                                    validator: changeValueProps.validator,
                                }}
                            />
                        ) : (
                            <>
                                <div css={mainDetailsContainerStyle}>
                                    <div>
                                        <div>{user.FirstName} {user.Surname}</div>
                                        <div>{user.Email}</div>
                                        <div>Password: **********</div>
                                    </div>
                                    <div>
                                        <div onClick={() => setDataChanging(data.NAME)}>Edit name</div>
                                        <div onClick={() => setDataChanging(data.EMAIL)}>Change email</div>
                                        <div onClick={() => setDataChanging(data.PASSWORD)}>Change password</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {tabSelected.key === tabs.VIDEOS.key && (
                    <div>
                        <div css={titleStyle}>{tabSelected.label}</div>
                        <div>
                            {orderedOnlineEvents?.length ? (
                                orderedOnlineEvents.map((ev: OnlineEventPage) => (
                                    <React.Fragment key={ev.id}>
                                        <Link to={`online-event/${ev.uRLSegment}`}>
                                            {ev.title}
                                        </Link>
                                    </React.Fragment>
                                )
                            )) : "There are no events to display."}
                        </div>
                    </div>
                )}
                {tabSelected.key === tabs.ORDERS.key && (
                    <div>
                        <div css={titleStyle}>{tabSelected.label}</div>
                        <div css={ordersContainerStyle}>
                            {orders?.length ? (
                                orders.map((order: Order) => (
                                    <div key={order.id}>
                                        <div>Order ID: {order.id}</div>
                                        <div>Status: {order.status}</div>
                                        <div>Total paid: {order.countryCode?.currency ?? "$"}{order.total}</div>
                                        <div>
                                            <div>Products:</div>
                                            <ul css={productListStyle}>
                                                {order.products.map((product: Product) => (
                                                    <li key={product.id}>{product.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ))
                            ) : "There are no orders to display."}
                        </div>
                    </div>
                )}
                <RoundButton onButtonClick={onLogOut}>Log out</RoundButton>
            </div>
        </div>
    );
}

const isPasswordLongEnough = (password: string): boolean => trim(password).length >= 10;

const passwordValidator = (fields: any, isSubmit: boolean = false) => {
    const errors: any = {};

    if (!fields.currentPassword) {
        errors.currentPassword = 'Please provide your current password.';
    }

    if (!fields.setPassword) {
        errors.setPassword = 'Please provide a new password.';
    }

    if (!fields.confirmPassword) {
        errors.confirmPassword = 'Please confirm your new password.';
    }

    if (fields.setPassword && !isPasswordLongEnough(fields.setPassword) && isSubmit) {
        errors.setPassword = 'Your password does not meet the requirements.';
    }

    if (
        isSubmit &&
        (fields.setPassword !== fields.confirmPassword) &&
        (!isEmpty(fields.setPassword) && !isEmpty(fields.confirmPassword))
    ) {
        errors['confirmPassword'] = "These passwords don’t match.";
    } else if (
        isSubmit &&
        (fields.setPassword === fields.currentPassword) &&
        (!isEmpty(fields.setPassword) && !isEmpty(fields.currentPassword))
    ) {
        errors['setPassword'] = "The new password must be different to the current password.";
    }

    return errors;
};

const ORDERED_ONLINE_EVENTS_QUERY = gql`
    query orderedOnlineEvents($userId: String!) {
        orderedOnlineEvents(userId: $userId) {
            id
            title
            uRLSegment
        }
    }
`;

const ORDERS_QUERY = gql`
    query orders($userId: String!) {
        orders(userId: $userId) {
            id
            status
            total
            countryCode {
                id
                currency
            }
            products {
                id
                title
            }
        }
    }
`;

const CHANGE_NAME_MUTATION = gql`
    mutation changeNameHomeUser($firstName: String!, $surname: String!) {
        changeNameHomeUser(firstName: $firstName, surname: $surname) {
            id
        }
    }
`;

const CHANGE_EMAIL_MUTATION = gql`
    mutation changeEmailHomeUser($currentPassword: String!, $email: String!, $confirmEmail: String!, $logoutAllDevices: Boolean!) {
        changeEmailHomeUser(currentPassword: $currentPassword, email: $email, confirmEmail: $confirmEmail, logoutAllDevices: $logoutAllDevices) {
            id
        }
    }
`;

const CHANGE_PASSWORD_MUTATION = gql`
    mutation changePasswordHomeUser($currentPassword: String!, $setPassword: String!, $confirmPassword: String!, $logoutAllDevices: Boolean!) {
        changePasswordHomeUser(currentPassword: $currentPassword, setPassword: $setPassword, confirmPassword: $confirmPassword, logoutAllDevices: $logoutAllDevices) {
            id
        }
    }
`;

const tabs = {
    ACCOUNT_OVERVIEW: {
        key: "account-overview",
        label: "Account Overview"
    },
    VIDEOS: {
        key: "videos",
        label: "Videos"
    },
    ORDERS: {
        key: "orders",
        label: "Orders"
    },
}

const data = {
    NAME: {
        key: "name",
        label: "Name",
    },
    EMAIL: {
        key: "email",
        label: "Email",
    },
    PASSWORD: {
        key: "password",
        label: "Password",
    },
}

const style = css`
    display: flex;
    justify-content: space-evenly;
`;

const accountOverviewContainerStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const mainDetailsContainerStyle = css`
    display: flex;
    gap: 50px;
`;

const tabLinksStyle = css`
    display: flex;
    flex-direction: column;
`;

const productListStyle = css`
    list-style: disc;
    list-style-position: inside;
`;

const ordersContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 30px;
`;

const titleStyle = css`
    font-size: 24px;
`;
export default Manage;
